import { useEffect, useRef } from 'react';
import { DEFAULT_SANDSTORM_CONFIG } from '../layout/SandstormBackground';

// Shared shaders (same rendering as SandstormBackground)
const vertexShader = /* glsl */`
  attribute float aSize;
  attribute float aAlpha;
  varying float vAlpha;
  void main() {
    vAlpha = aAlpha;
    gl_PointSize = aSize;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */`
  precision mediump float;
  uniform vec3 uColor;
  varying float vAlpha;
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float soft = 1.0 - smoothstep(0.2, 0.5, d);
    gl_FragColor = vec4(uColor, vAlpha * soft);
  }
`;

// Props:
//   originX, originY — cursor position in viewport px (center of explosion)
//   color            — hex color string
//   count            — number of particles
//   onComplete(config) — called when animation ends, with fresh sandstorm config
export function StarBurstAnimation({ originX, originY, color, count, onComplete }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;

    let animId;
    let cleanup = () => {};
    let active = true;

    import('three').then((THREE) => {
      if (!active) return;

      const w = el.clientWidth;
      const h = el.clientHeight;

      // Convert viewport origin to Three.js pixel-space (center = 0, y-flipped)
      const ox = (originX ?? w / 2) - w / 2;
      const oy = -(((originY ?? h / 2) - h / 2));

      // Renderer
      const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h);
      el.appendChild(renderer.domElement);

      const camera = new THREE.OrthographicCamera(-w/2, w/2, h/2, -h/2, 0.1, 10);
      camera.position.z = 1;
      const scene = new THREE.Scene();

      // Particle buffers
      const n   = Math.max(64, count ?? 256);
      const pos = new Float32Array(n * 3);
      const vel = new Float32Array(n * 2); // vx, vy per particle
      const al  = new Float32Array(n);
      const sz  = new Float32Array(n);

      for (let i = 0; i < n; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 80 + Math.random() * 620;
        pos[i*3]     = ox;
        pos[i*3 + 1] = oy;
        pos[i*3 + 2] = 0;
        vel[i*2]     = Math.cos(angle) * speed;
        vel[i*2 + 1] = Math.sin(angle) * speed;
        al[i] = 0.65 + Math.random() * 0.35;
        sz[i] = 1.5  + Math.random() * 5;
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setAttribute('aSize',    new THREE.BufferAttribute(sz,  1));
      geo.setAttribute('aAlpha',   new THREE.BufferAttribute(al,  1));

      const mat = new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: { uColor: { value: new THREE.Color(color ?? '#f0ae44') } },
        transparent: true,
        depthWrite: false,
      });

      scene.add(new THREE.Points(geo, mat));

      const DRAG        = 0.985; // speed multiplier per frame (~keeps going ~2–3s at 60fps)
      const FADE        = 0.28;  // alpha units lost per second
      const MAX_MS      = 4500;
      const MARGIN      = 80;
      let last          = performance.now();
      const startTime   = last;
      let completed     = false;

      function tick(now) {
        animId = requestAnimationFrame(tick);
        if (completed) return;

        const dt = Math.min((now - last) * 0.001, 0.05);
        last = now;

        let doneCount = 0;
        for (let i = 0; i < n; i++) {
          const pi = i * 3;
          const vi = i * 2;

          pos[pi]     += vel[vi]     * dt;
          pos[pi + 1] += vel[vi + 1] * dt;
          vel[vi]     *= DRAG;
          vel[vi + 1] *= DRAG;
          al[i] = Math.max(0, al[i] - FADE * dt);

          if (
            al[i] <= 0 ||
            pos[pi]     >  w / 2 + MARGIN ||
            pos[pi]     < -w / 2 - MARGIN ||
            pos[pi + 1] >  h / 2 + MARGIN ||
            pos[pi + 1] < -h / 2 - MARGIN
          ) doneCount++;
        }

        geo.attributes.position.needsUpdate = true;
        geo.attributes.aAlpha.needsUpdate   = true;
        renderer.render(scene, camera);

        if (!completed && (doneCount >= n || now - startTime >= MAX_MS)) {
          completed = true;
          onComplete?.({ ...DEFAULT_SANDSTORM_CONFIG });
        }
      }

      animId = requestAnimationFrame(tick);

      cleanup = () => {
        cancelAnimationFrame(animId);
        geo.dispose();
        mat.dispose();
        renderer.dispose();
        if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
      };
    });

    return () => {
      active = false;
      cleanup();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={mountRef}
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
}
